import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import ProductCard from "../components/ProductCard";
import { Input } from "../components/ui/input";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../components/ui/select";
import { Search } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AllProducts() {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [query, setQuery] = useState("");
    const [category, setCategory] = useState("all");
    const [sort, setSort] = useState("featured");

    useEffect(() => {
        axios.get(`${API}/products`).then((r) => {
            setProducts(r.data);
            setLoading(false);
        }).catch(() => setLoading(false));
    }, []);

    const categories = useMemo(() => {
        const set = new Set(products.map((p) => p.category));
        return ["all", ...Array.from(set)];
    }, [products]);

    const filtered = useMemo(() => {
        let list = [...products];
        if (query.trim()) {
            list = list.filter((p) =>
                p.name.toLowerCase().includes(query.toLowerCase()) ||
                p.description.toLowerCase().includes(query.toLowerCase())
            );
        }
        if (category !== "all") {
            list = list.filter((p) => p.category === category);
        }
        if (sort === "price-asc") list.sort((a, b) => a.price - b.price);
        if (sort === "price-desc") list.sort((a, b) => b.price - a.price);
        if (sort === "name") list.sort((a, b) => a.name.localeCompare(b.name));
        return list;
    }, [products, query, category, sort]);

    return (
        <div className="pt-32 pb-24 min-h-screen" data-testid="all-products-page">
            <div className="max-w-7xl mx-auto px-6 md:px-12">
                <div className="mb-12">
                    <p className="text-pep-primary text-sm uppercase tracking-widest font-medium mb-3">Catalog</p>
                    <h1 className="font-heading text-5xl sm:text-6xl tracking-tighter text-pep-navy font-medium">
                        All Products
                    </h1>
                    <p className="text-pep-muted mt-4 text-lg max-w-2xl">
                        The full PepStep catalog. Every product independently lab-tested with a Certificate of Analysis available on request.
                    </p>
                </div>

                <div className="flex flex-col md:flex-row gap-4 mb-12 p-4 bg-white rounded-3xl border border-pep-border/60 sticky top-20 z-30 backdrop-blur">
                    <div className="flex-1 relative">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-pep-muted" />
                        <Input
                            placeholder="Search peptides..."
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            className="pl-11 rounded-full border-pep-border bg-pep-surface focus-visible:ring-pep-primary"
                            data-testid="all-search-input"
                        />
                    </div>
                    <Select value={category} onValueChange={setCategory}>
                        <SelectTrigger className="md:w-48 rounded-full bg-pep-surface border-pep-border" data-testid="all-category-select">
                            <SelectValue placeholder="Category" />
                        </SelectTrigger>
                        <SelectContent>
                            {categories.map((c) => (
                                <SelectItem key={c} value={c} data-testid={`category-${c}`}>
                                    {c === "all" ? "All Categories" : c.charAt(0).toUpperCase() + c.slice(1)}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    <Select value={sort} onValueChange={setSort}>
                        <SelectTrigger className="md:w-48 rounded-full bg-pep-surface border-pep-border" data-testid="all-sort-select">
                            <SelectValue placeholder="Sort by" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="featured">Featured</SelectItem>
                            <SelectItem value="price-asc">Price: Low to High</SelectItem>
                            <SelectItem value="price-desc">Price: High to Low</SelectItem>
                            <SelectItem value="name">Name (A-Z)</SelectItem>
                        </SelectContent>
                    </Select>
                </div>

                {loading ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {Array.from({ length: 9 }).map((_, i) => (
                            <div key={i} className="rounded-3xl bg-pep-surface aspect-[3/4] animate-pulse" />
                        ))}
                    </div>
                ) : filtered.length === 0 ? (
                    <div className="text-center py-20 text-pep-muted" data-testid="no-products">
                        No products match your filters.
                    </div>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {filtered.map((p, i) => (
                            <ProductCard key={p.id} product={p} index={i} />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
